import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import {
  type CustomFieldRecord,
  TargetEntityType,
} from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import {
  useDatasetMetadataKeys,
  useDatasetTags,
} from "@/shared/domain/datasets/hooks";
import {
  useEventMetadataKeys,
  useEventNames,
  useEventTags,
} from "@/shared/domain/events/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  useFileMetadataKeys,
  useOrgFileTags,
} from "@/shared/domain/files/hooks";
import {
  MIN_TOPIC_PREFIX_LENGTH,
  TOPIC_PREFIX_DEBOUNCE_MS,
  useOrgMessagePaths,
  useOrgTopicNames,
} from "@/shared/domain/topics/hooks";
import { useDebouncedCallback } from "@/shared/hooks";

import { QueryTarget, queryTargetToEntityType } from "../QueryRecord";
import { type RoboqlExternalCtx } from "../RoboqlExternalCtx";

/**
 * The RoboQL editor's custom-field inputs for a target: the resolved
 * `release-custom-fields` flag and the target's Ready custom fields.
 *
 * `customFields` is populated ONLY for targets that support custom fields
 * (Datasets/Events, per {@link queryTargetToEntityType}) and is `undefined`
 * otherwise. That is a load-bearing contract: the completion provider gates the
 * `custom` namespace and the custom `SORT BY` keys on the presence of this
 * handle, so an unsupported target must never receive one. `useReadyCustomFields`
 * requires a concrete entity type and must run every render (rules of hooks), so
 * a `Dataset` placeholder is passed with `enabled: false` for unsupported targets
 * and its never-fetched result is discarded here.
 */
const useRoboqlCustomFields = (
  target: QueryTarget,
  orgId: string,
): {
  customFieldsEnabled: boolean;
  customFields?: UseQueryResult<CustomFieldRecord[], Error>;
} => {
  const customFieldsEnabled = useCheckFeatureFlag("release-custom-fields");
  const customFieldEntityType = queryTargetToEntityType(target);
  const targetSupportsCustomFields = customFieldEntityType !== undefined;
  const customFieldsQuery = useReadyCustomFields({
    entityType: customFieldEntityType ?? TargetEntityType.Dataset,
    orgId,
    enabled: targetSupportsCustomFields,
  });

  return {
    customFieldsEnabled,
    customFields: targetSupportsCustomFields ? customFieldsQuery : undefined,
  };
};

export const useRoboqlExternalCtx = (
  target: QueryTarget,
  orgId: string,
): RoboqlExternalCtx => {
  const fileTagsQuery = useOrgFileTags(orgId);
  const fileMetadataKeysQuery = useFileMetadataKeys(orgId);
  const datasetTagsQuery = useDatasetTags(orgId);
  const datasetMetadataKeysQuery = useDatasetMetadataKeys(orgId);
  const topicNamesQuery = useOrgTopicNames(orgId);
  const messagePathsQuery = useOrgMessagePaths(orgId);

  // Per-keystroke, prefix-scoped lookups for the completion provider.
  //
  // Debounced here rather than in a hook because this *is* the keystroke
  // callback: monaco re-invokes its completion provider on every character, and
  // each distinct prefix is a distinct fetch, so undebounced this issues one
  // request per keystroke. A hook cannot cover it — the typed text is known
  // inside the provider, not in React.
  //
  // Routed through `fetchQuery` rather than a second `useQuery` so it shares one
  // cache with `useOrgMessagePaths` above: an already-fetched prefix is served
  // from memory and concurrent identical lookups dedupe.
  const queryClient = useQueryClient();
  const { topics: topicService } = useDomainServices();
  const pendingLookup = React.useRef<{
    prefix: string;
    resolve: (paths: string[]) => void;
    reject: (reason: unknown) => void;
  } | null>(null);

  const runLookup = React.useCallback(() => {
    const lookup = pendingLookup.current;
    pendingLookup.current = null;
    if (lookup === null) {
      return;
    }
    queryClient
      .fetchQuery({
        queryKey: ["orgMessagePaths", orgId, lookup.prefix],
        queryFn: ({ signal }) =>
          topicService.getMessagePathsForOrg({
            abortSignal: signal,
            resourceOwnerId: orgId,
            prefix: lookup.prefix,
          }),
        staleTime: 30000,
      })
      .then(lookup.resolve, lookup.reject);
  }, [queryClient, topicService, orgId]);

  const debouncedLookup = useDebouncedCallback(
    runLookup,
    TOPIC_PREFIX_DEBOUNCE_MS,
  );

  const fetchMessagePaths = React.useCallback(
    (prefix: string): Promise<string[]> => {
      if (prefix.length < MIN_TOPIC_PREFIX_LENGTH) {
        return Promise.resolve([]);
      }
      // Settle the superseded keystroke rather than dropping it: monaco is
      // already showing the newer request's results, but a promise nobody ever
      // resolves would keep its completion session waiting forever.
      pendingLookup.current?.resolve([]);
      return new Promise<string[]>((resolve, reject) => {
        pendingLookup.current = { prefix, resolve, reject };
        debouncedLookup();
      });
    },
    [debouncedLookup],
  );
  const eventNamesQuery = useEventNames(orgId);
  const eventTagsQuery = useEventTags(orgId);
  const eventMetadataKeysQuery = useEventMetadataKeys(orgId);

  const { customFieldsEnabled, customFields } = useRoboqlCustomFields(
    target,
    orgId,
  );

  return {
    target,
    customFieldsEnabled,
    customFields,
    datasets: {
      tags: datasetTagsQuery,
      metadataKeys: datasetMetadataKeysQuery,
    },
    files: {
      tags: fileTagsQuery,
      metadataKeys: fileMetadataKeysQuery,
    },
    topics: {
      names: topicNamesQuery,
      messagePaths: messagePathsQuery,
      fetchMessagePaths,
    },
    events: {
      names: eventNamesQuery,
      tags: eventTagsQuery,
      metadataKeys: eventMetadataKeysQuery,
    },
  };
};
